import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { styled } from '@mui/material/styles';

/**
 * The phone's day view with the days either side drawn next to it.
 *
 * A swipe drags all three together, so the day being swiped to slides in beside
 * the one leaving, lined up at the same time of day. Releasing past a quarter
 * of the width, or with a flick, finishes the move; anything less springs back.
 * The neighbours are real day views rendered inert, and each keeps its instance
 * as it becomes the current day, so the hand-over is invisible.
 */

const GAP = 14;
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const SETTLE_MS = 300;
/** Share of the width past which a release finishes the move. */
const COMMIT_SHARE = 0.25;
/** px/ms. A flick finishes the move even when it's short. */
const FLICK = 0.45;

const Viewport = styled('div')({
  // clip, not hidden: hidden makes a scroll container and unpins every sticky
  // date bar and card title inside.
  overflowX: 'clip',
  // The day owns the horizontal axis; vertical stays the page's scroll.
  touchAction: 'pan-y',
});

const Strip = styled('div')({
  display: 'flex',
  gap: GAP,
  width: '100%',
  // Parked one pane to the left by margin, not transform, so nothing inside
  // gains a new containing block while the day is at rest.
  marginLeft: `calc(-100% - ${GAP}px)`,
});

const Slot = styled('div')({
  flex: '0 0 100%',
  minWidth: 0,
});

/** The box that actually scrolls: My Schedule's own, or the window. */
function scrollBy(node: HTMLElement | null, top: number): void {
  for (let el = node?.parentElement ?? null; el; el = el.parentElement) {
    const overflow = getComputedStyle(el).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && el.scrollHeight > el.clientHeight + 4) {
      el.scrollBy({ top });
      return;
    }
  }
  window.scrollBy({ top });
}

export interface DayPane {
  key: string;
  node: ReactNode;
}

interface Props {
  prev: DayPane | null;
  current: DayPane;
  next: DayPane | null;
  onPrev: () => void;
  onNext: () => void;
}

type Leads = [number, number, number];

export default function DaySwipe({ prev, current, next, onPrev, onNext }: Props) {
  const viewport = useRef<HTMLDivElement | null>(null);
  const strip = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const [dx, setDx] = useState(0);
  const [settling, setSettling] = useState(false);
  const [moving, setMoving] = useState(false);

  /** Space above each pane's hours, so all three show the same time at the same height. */
  const [leads, setLeads] = useState<Leads>([0, 0, 0]);
  const liveLeads = useRef<Leads>(leads);
  /** The lead the current pane was last drawn with. */
  const drawnLead = useRef(0);

  /**
   * Holds the current day's hours still when its lead changes: the space added
   * above them at the start of a swipe, or removed once it's over, is scrolled
   * away in the same frame.
   */
  useLayoutEffect(() => {
    liveLeads.current = leads;
    const change = leads[1] - drawnLead.current;
    drawnLead.current = leads[1];
    if (change) scrollBy(viewport.current, change);
  }, [leads]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  /** Measures where each pane's hours begin and lines them up with the lowest. */
  const align = useCallback(() => {
    const slots = strip.current ? (Array.from(strip.current.children) as HTMLElement[]) : [];
    const natural = slots.map((slot, i) => {
      const column = slot.querySelector<HTMLElement>('[data-day-column]');
      if (!column) return null;
      return column.getBoundingClientRect().top - slot.getBoundingClientRect().top - liveLeads.current[i];
    });
    const lowest = Math.max(0, ...natural.filter((n): n is number => n != null));
    setLeads(natural.map((n) => (n == null ? 0 : lowest - n)) as Leads);
  }, []);

  const settle = useCallback(
    (to: number, go: 'prev' | 'next' | null) => {
      setSettling(true);
      setDx(to);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        // The pane about to become current was drawn with its own lead.
        if (go === 'next') drawnLead.current = liveLeads.current[2];
        if (go === 'prev') drawnLead.current = liveLeads.current[0];
        if (go === 'next') onNext();
        if (go === 'prev') onPrev();
        setSettling(false);
        setMoving(false);
        setDx(0);
        setLeads([0, 0, 0]);
      }, SETTLE_MS);
    },
    [onNext, onPrev],
  );

  const onPointerDown = (e: ReactPointerEvent) => {
    if (settling || (e.pointerType === 'mouse' && e.button !== 0)) return;

    const width = viewport.current?.clientWidth ?? window.innerWidth;
    const startX = e.clientX;
    const startY = e.clientY;
    const id = e.pointerId;
    let claimed = false;
    let offset = 0;
    let last = { x: startX, t: e.timeStamp };
    let velocity = 0;

    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('pointerdown', another);
    };

    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== id) return;
      const moved = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!claimed) {
        if (Math.abs(dy) > 10 && Math.abs(dy) >= Math.abs(moved)) return stop();
        if (Math.abs(moved) < 14) return;
        claimed = true;
        setMoving(true);
        align();
      }
      // Nothing to reveal at the ends of the week, so it resists.
      const blocked = (moved < 0 && !next) || (moved > 0 && !prev);
      offset = blocked ? moved * 0.18 : moved;
      const dt = ev.timeStamp - last.t;
      if (dt > 0) velocity = (ev.clientX - last.x) / dt;
      last = { x: ev.clientX, t: ev.timeStamp };
      setDx(offset);
    };

    const cancel = () => {
      stop();
      if (claimed) settle(0, null);
    };

    // A second finger makes this a pinch, which is the hour spacing's.
    const another = (ev: PointerEvent) => {
      if (ev.pointerId !== id) cancel();
    };

    const end = (ev: PointerEvent) => {
      if (ev.pointerId !== id) return;
      stop();
      if (!claimed) return;

      // The finger lifted off a card: that was a swipe, not a tap on it.
      const swallow = (click: MouseEvent) => {
        click.stopPropagation();
        click.preventDefault();
      };
      window.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 350);

      // A finger that paused before lifting isn't flicking.
      const flick = ev.timeStamp - last.t < 90 ? velocity : 0;
      if (next && (offset < -width * COMMIT_SHARE || (flick < -FLICK && offset < -30))) {
        settle(-(width + GAP), 'next');
      } else if (prev && (offset > width * COMMIT_SHARE || (flick > FLICK && offset > 30))) {
        settle(width + GAP, 'prev');
      } else {
        settle(0, null);
      }
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('pointerdown', another);
  };

  const panes = [prev ?? { key: 'none:prev', node: null }, current, next ?? { key: 'none:next', node: null }];

  return (
    <Viewport ref={viewport} onPointerDown={onPointerDown} data-own-swipe>
      <Strip
        ref={strip}
        style={{
          transform: moving ? `translateX(${dx}px)` : undefined,
          transition: settling ? `transform ${SETTLE_MS}ms ${EASE}` : 'none',
        }}
      >
        {panes.map((pane, i) => (
          <Slot
            key={pane.key}
            inert={i !== 1}
            aria-hidden={i !== 1 || undefined}
            style={{ '--column-lead': `${leads[i]}px` } as CSSProperties}
          >
            {pane.node}
          </Slot>
        ))}
      </Strip>
    </Viewport>
  );
}
